import { FC, Fragment } from "react";
import { Stack } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useEffectOnceWhen } from "rooks";
import { LiveUsageResourceGridCsvExport } from "./LiveUsageResourceGridCsvExport";
import { LiveUsageResourceGridToolbarDropDownActions } from "./actions/LiveUsageResourceGridToolbarDropDownActions";
import { LiveUsageMgmtResourceCardStatistics } from "./statistics/LiveUsageMgmtResourceCardStatistics";
import { LiveUsageMgmtCreateWorkflowToolbarAction } from "./actions/LiveUsageMgmtCreateWorkflowToolbarAction";
import { allResourcesResourceType } from "../../../utils/constants/common";
import {
  resourceIdSearchParam,
  resourceTypeSearchParam,
} from "../../../utils/constants/searchParams";
import { AGGridColumnsToolPanel } from "../../../../common/ag-grid/components/AGGridColumnsToolPanel";
import { AgGridGlobalSearch } from "../../../../common/ag-grid/components/global-search/AgGridGlobalSearch";
import type { GridApi } from "ag-grid-community";
import { ResourceType } from "@/services/cloudchipr.api";
import { LiveResourcesGridFilterContent } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/LiveResourcesGridFilterContent";
import { LiveUsageResourceGridToolbarQuickActions } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/LiveUsageResourceGridToolbarQuickActions";
import { setLiveUsageMgmtViewWithoutFiltersResourceTypes } from "@/store/live-usage-mgmt/liveUsageMgmtSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateGridResourceDataThunk } from "@/store/resources-grid/thunks/data/updateGridResourceDataThunk";
import { gridResourcesTotalCountSelector } from "@/store/resources-grid/selectors/resource-data/counts/gridResourcesTotalCountSelector";
import { gridResourcesFilteredCountSelector } from "@/store/resources-grid/selectors/resource-data/counts/gridResourcesFilteredCountSelector";

interface LiveUsageResourceGridToolbarProps {
  masterResourceType: ResourceType;
  gridApi: GridApi | null;
  masterGridId: string;
}

export const LiveUsageResourceGridToolbar: FC<
  LiveUsageResourceGridToolbarProps
> = ({ masterResourceType, gridApi, masterGridId }) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const resourceTypeFromSearchParam = searchParams.get(resourceTypeSearchParam);
  const pathResourceIds = searchParams.get(resourceIdSearchParam);
  const needToFilterById = !!(
    resourceTypeFromSearchParam === masterResourceType && pathResourceIds
  );

  const resourcesTotalCount = useAppSelector((state) =>
    gridResourcesTotalCountSelector(state, masterGridId),
  );
  const resourcesCount = useAppSelector((state) =>
    gridResourcesFilteredCountSelector(state, masterGridId),
  );

  useEffectOnceWhen(() => {
    dispatch(
      setLiveUsageMgmtViewWithoutFiltersResourceTypes({
        resourceType: masterResourceType,
        value: true,
      }),
    );
    dispatch(updateGridResourceDataThunk(masterGridId));
  }, needToFilterById);

  return (
    <Fragment>
      <Stack
        p={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <LiveUsageMgmtResourceCardStatistics
          resourceType={masterResourceType}
          masterGridId={masterGridId}
        />

        <Stack direction="row" spacing={1}>
          {resourceTypeFromSearchParam === allResourcesResourceType && (
            <LiveUsageMgmtCreateWorkflowToolbarAction
              masterGridId={masterGridId}
              resourceType={masterResourceType}
            />
          )}
          <LiveUsageResourceGridToolbarDropDownActions
            masterGridId={masterGridId}
            masterResourceType={masterResourceType}
          />
        </Stack>
      </Stack>

      <Stack
        px={2}
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={0.5}>
          <LiveResourcesGridFilterContent
            masterGridId={masterGridId}
            filteredCount={resourcesCount}
            totalCount={resourcesTotalCount}
            masterResourceType={masterResourceType}
          />
        </Stack>

        <Stack alignItems="center" direction="row" spacing={1}>
          <LiveUsageResourceGridToolbarQuickActions
            masterGridId={masterGridId}
          />

          <AgGridGlobalSearch
            gridApi={gridApi}
            searchValue={pathResourceIds ?? ""}
          />

          <LiveUsageResourceGridCsvExport
            gridApi={gridApi}
            resourceType={masterResourceType}
          />

          <AGGridColumnsToolPanel gridApi={gridApi} />
        </Stack>
      </Stack>
    </Fragment>
  );
};
